import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaCheckCircle, FaExclamationTriangle, FaPlus, FaSearch, FaUndo, FaFileExcel, FaEdit, FaTrash, FaShieldAlt } from "react-icons/fa";
import * as XLSX from "xlsx";
import "./DeviceTracker.css";

// eslint-disable-next-line no-undef
const API = process.env.REACT_APP_BASE_API_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const localInputValue = () => {
  const d = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
};

const toLocalInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const toLocalDateOnlyInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const displayDate = (value) => value ? new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";

const displayDateOnly = (value) => value ? new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(value)) : "—";

const getCalibrationStatus = (expiryDate) => {
  if (!expiryDate) return { label: "Not Set", className: "cal-unspecified", diffDays: null };
  const exp = new Date(expiryDate);
  const now = new Date();
  exp.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  const diffDays = Math.round((exp - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: "EXPIRED", className: "cal-expired", diffDays };
  if (diffDays <= 30) return { label: `Expiring Soon (${diffDays}d)`, className: "cal-expiring", diffDays };
  return { label: "Valid", className: "cal-valid", diffDays };
};

const DeviceTracker = () => {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loans, setLoans] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [driverSearch, setDriverSearch] = useState("");
  const [driverMenuOpen, setDriverMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [query, setQuery] = useState("");
  const [type, setType] = useState("ALL");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const [lend, setLend] = useState({
    device_id: "",
    jp_number: "",
    driver_name: "",
    phone_number: "",
    offloading_location: "",
    issued_at: localInputValue(),
    expected_return_at: "",
    condition_out: "GOOD",
    remarks: ""
  });
  const [briefingConfirmed, setBriefingConfirmed] = useState(false);
  const [receive, setReceive] = useState({ returned_at: localInputValue(), condition_in: "GOOD", return_remarks: "" });

  const [deviceModal, setDeviceModal] = useState(false);
  const [deviceForm, setDeviceForm] = useState({
    device_id: null,
    device_number: "",
    device_type: "",
    serial_number: "",
    calibration_expiry: "",
    operational_status: "ACTIVE"
  });

  const [addDeviceModal, setAddDeviceModal] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [newDevice, setNewDevice] = useState({
    device_number: "",
    device_type: "H2S",
    custom_type: "",
    serial_number: "",
    calibration_expiry: "",
    operational_status: "ACTIVE"
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [deviceRes, loanRes, driverRes] = await Promise.all([
        axios.get(`${API}/device-tracker/devices`, auth()),
        axios.get(`${API}/device-tracker/loans`, auth()),
        axios.get(`${API}/drivers-list`, auth()),
      ]);
      setDevices(deviceRes.data.data || []);
      setLoans(loanRes.data.data || []);
      setDrivers((driverRes.data.data || []).filter((driver) => driver.is_active === "Y"));
    } catch (e) {
      setNotice({ kind: "error", text: e.response?.data?.message || "Unable to load device tracker." });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const available = devices.filter((d) => d.availability_status === "AVAILABLE" && d.operational_status === "ACTIVE");
  const active = loans.filter((l) => l.loan_status === "ISSUED");
  const overdue = active.filter((l) => l.expected_return_at && new Date(l.expected_return_at) < new Date());
  const h2sCount = devices.filter((d) => d.device_type === "H2S").length;
  const thurayaCount = devices.filter((d) => d.device_type === "THURAYA").length;

  const deviceTypes = useMemo(() => {
    const set = new Set(["H2S", "THURAYA"]);
    devices.forEach((d) => {
      if (d.device_type) set.add(d.device_type.trim().toUpperCase());
    });
    return Array.from(set);
  }, [devices]);

  const inventoryTypeSummary = useMemo(() => {
    const counts = {};
    devices.forEach((d) => {
      const t = d.device_type || "OTHER";
      counts[t] = (counts[t] || 0) + 1;
    });
    return Object.entries(counts).map(([t, count]) => `${count} ${t}`).join(" · ");
  }, [devices]);

  const rows = tab === "inventory" ? devices : tab === "active" ? active : loans;
  const filtered = useMemo(() => rows.filter((r) => {
    if (type !== "ALL" && r.device_type !== type) return false;
    const haystack = Object.values(r).join(" ").toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  }), [rows, query, type]);

  const filteredDrivers = useMemo(() => {
    const term = driverSearch.trim().toLowerCase();
    return drivers.filter((driver) => `${driver.driver_name || ""} ${driver.reporting_driver_code || ""}`.toLowerCase().includes(term));
  }, [drivers, driverSearch]);

  const selectDriver = (driver) => {
    setLend((current) => ({ ...current, driver_name: driver.driver_name }));
    setDriverSearch(driver.driver_name);
    setDriverMenuOpen(false);
  };

  const selectedLendDevice = (modal === "edit" ? selected : available.find((d) => String(d.device_id) === String(lend.device_id)))
    || devices.find((d) => String(d.device_id) === String(lend.device_id));
  const isLendH2S = selectedLendDevice?.device_type === "H2S";
  const isLendThuraya = selectedLendDevice?.device_type === "THURAYA";

  const toast = (text, kind = "success") => { setNotice({ text, kind }); setTimeout(() => setNotice(null), 5000); };

  const submitLend = async (e) => {
    e.preventDefault();
    if (!lend.driver_name) {
      toast("Please select a driver from the driver list.", "error");
      setDriverMenuOpen(true);
      return;
    }
    const targetDevice = (modal === "edit" ? selected : available.find((d) => String(d.device_id) === String(lend.device_id)))
      || devices.find((d) => String(d.device_id) === String(lend.device_id));
    const isH2S = targetDevice?.device_type === "H2S";
    const isThuraya = targetDevice?.device_type === "THURAYA";

    if (modal !== "edit" && isH2S && !briefingConfirmed) {
      toast("Mandatory check: Please confirm that the driver has been briefed on H₂S requirements before issuing the monitor.", "error");
      return;
    }
    if (modal !== "edit" && isThuraya && !briefingConfirmed) {
      toast("Mandatory check: Please confirm that the driver has been briefed on Thuraya satellite phone use before issuing the phone.", "error");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...lend,
        jp_number: lend.jp_number ? lend.jp_number.trim() : null,
        issued_at: new Date(lend.issued_at).toISOString(),
        expected_return_at: lend.expected_return_at ? new Date(lend.expected_return_at).toISOString() : null
      };
      if (modal === "edit") await axios.patch(`${API}/device-tracker/loans/${selected.loan_id}`, payload, auth());
      else await axios.post(`${API}/device-tracker/loans`, payload, auth());
      setModal(null);
      setDriverSearch("");
      setBriefingConfirmed(false);
      setLend({
        device_id: "",
        jp_number: "",
        driver_name: "",
        phone_number: "",
        offloading_location: "",
        issued_at: localInputValue(),
        expected_return_at: "",
        condition_out: "GOOD",
        remarks: ""
      });
      toast(modal === "edit" ? "Lending record updated successfully." : "Device issued successfully.");
      await load();
    } catch (e2) {
      toast(e2.response?.data?.message || `Could not ${modal === "edit" ? "update" : "issue"} device.`, "error");
    } finally {
      setSaving(false);
    }
  };

  const submitReceive = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const result = await axios.patch(`${API}/device-tracker/loans/${selected.loan_id}/receive`, { ...receive, returned_at: new Date(receive.returned_at).toISOString() }, auth());
      setModal(null);
      toast(result.data.message || "Device received successfully.");
      await load();
    } catch (e2) {
      toast(e2.response?.data?.message || "Could not receive device.", "error");
    } finally {
      setSaving(false);
    }
  };

  const openEditDevice = (device) => {
    setDeviceForm({
      device_id: device.device_id,
      device_number: device.device_number,
      device_type: device.device_type,
      custom_type: "",
      serial_number: device.serial_number || "",
      calibration_expiry: toLocalDateOnlyInput(device.calibration_expiry),
      operational_status: device.operational_status || "ACTIVE"
    });
    setDeviceModal(true);
  };

  const submitEditDevice = async (e) => {
    e.preventDefault();
    const resolvedType = (deviceForm.device_type === "NEW_TYPE" ? deviceForm.custom_type : deviceForm.device_type)
      ?.trim().toUpperCase();
    if (!resolvedType) {
      toast("Please specify a valid device type.", "error");
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/device-tracker/devices/${deviceForm.device_id}`, {
        device_type: resolvedType,
        serial_number: deviceForm.serial_number ? deviceForm.serial_number.trim() : null,
        calibration_expiry: deviceForm.calibration_expiry ? deviceForm.calibration_expiry : null,
        operational_status: deviceForm.operational_status
      }, auth());
      setDeviceModal(false);
      toast(`Device ${deviceForm.device_number} updated successfully.`);
      await load();
    } catch (err) {
      toast(err.response?.data?.message || "Could not update device details.", "error");
    } finally {
      setSaving(false);
    }
  };

  const openAddDevice = () => {
    setNewDevice({
      device_number: "",
      device_type: (type !== "ALL" && deviceTypes.includes(type)) ? type : "H2S",
      custom_type: "",
      serial_number: "",
      calibration_expiry: "",
      operational_status: "ACTIVE"
    });
    setAddDeviceModal(true);
  };

  const submitAddDevice = async (e) => {
    e.preventDefault();
    if (!newDevice.device_number.trim()) {
      toast("Device number is required.", "error");
      return;
    }
    const resolvedType = (newDevice.device_type === "NEW_TYPE" ? newDevice.custom_type : newDevice.device_type)
      ?.trim().toUpperCase();
    if (!resolvedType) {
      toast("Please enter the new device type name.", "error");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/device-tracker/devices`, {
        device_number: newDevice.device_number.trim(),
        device_type: resolvedType,
        serial_number: newDevice.serial_number ? newDevice.serial_number.trim() : null,
        calibration_expiry: newDevice.calibration_expiry || null,
        operational_status: newDevice.operational_status || "ACTIVE"
      }, auth());
      setAddDeviceModal(false);
      setNewDevice({
        device_number: "",
        device_type: "H2S",
        custom_type: "",
        serial_number: "",
        calibration_expiry: "",
        operational_status: "ACTIVE"
      });
      toast(res.data.message || `Device "${newDevice.device_number.trim()}" added successfully.`);
      await load();
    } catch (err) {
      toast(err.response?.data?.message || "Could not add device.", "error");
    } finally {
      setSaving(false);
    }
  };

  const requestDeleteDevice = (device) => {
    if (device.availability_status === "ISSUED" || device.loan_id) {
      toast(`Cannot delete ${device.device_number} because it is currently issued to driver ${device.driver_name || ""}. Please receive it first.`, "error");
      return;
    }
    setDeviceToDelete(device);
  };

  const confirmDeleteDevice = async () => {
    if (!deviceToDelete) return;
    setSaving(true);
    try {
      const res = await axios.delete(`${API}/device-tracker/devices/${deviceToDelete.device_id}`, auth());
      toast(res.data.message || `Device ${deviceToDelete.device_number} deleted successfully.`);
      setDeviceToDelete(null);
      await load();
    } catch (err) {
      toast(err.response?.data?.message || "Could not delete device.", "error");
    } finally {
      setSaving(false);
    }
  };

  const exportRows = () => {
    let data;
    if (tab === "inventory") {
      data = filtered.map((r) => {
        const cal = getCalibrationStatus(r.calibration_expiry);
        return {
          "Device No.": r.device_number,
          "Type": r.device_type,
          "Serial Number": r.serial_number || "—",
          "Calibration Expiry": displayDateOnly(r.calibration_expiry),
          "Calibration Status": cal.label,
          "Operational Status": r.operational_status,
          "Availability Status": r.availability_status,
          "Current Driver": r.driver_name || "—",
          "Expected Return": displayDate(r.expected_return_at),
        };
      });
    } else {
      data = filtered.map((r) => ({
        "Device No.": r.device_number,
        "Type": r.device_type,
        "Serial Number": r.serial_number || "—",
        "JP Number": r.jp_number || "—",
        "Driver": r.driver_name || "",
        "Phone": r.phone_number || "",
        "Location": r.offloading_location || "",
        "Issued At": displayDate(r.issued_at),
        "Expected Return": displayDate(r.expected_return_at),
        "Returned At": displayDate(r.returned_at),
        "Status": r.loan_status || r.availability_status,
        "Condition Out": r.condition_out || "",
        "Condition In": r.condition_in || "",
        "Remarks": r.return_remarks || r.remarks || "",
      }));
    }
    const wb = XLSX.utils.book_new();
    const sheetName = tab === "inventory" ? "Device Inventory" : tab === "active" ? "Active Loans" : "Loan History";
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), sheetName);
    XLSX.writeFile(wb, `Device_Tracker_${tab}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const markReady = async (device) => {
    if (!window.confirm(`Confirm ${device.device_number} has been inspected and is ready to use?`)) return;
    try {
      await axios.patch(`${API}/device-tracker/devices/${device.device_id}/status`, { operational_status: "ACTIVE" }, auth());
      toast(`${device.device_number} is available again.`);
      await load();
    } catch (e) {
      toast(e.response?.data?.message || "Could not update device status.", "error");
    }
  };

  const openEdit = (loan) => {
    setSelected(loan);
    setLend({
      device_id: String(loan.device_id),
      jp_number: loan.jp_number || "",
      driver_name: loan.driver_name,
      phone_number: loan.phone_number,
      offloading_location: loan.offloading_location,
      issued_at: toLocalInput(loan.issued_at),
      expected_return_at: toLocalInput(loan.expected_return_at),
      condition_out: loan.condition_out || "GOOD",
      remarks: loan.remarks || ""
    });
    setDriverSearch(loan.driver_name);
    setDriverMenuOpen(false);
    setBriefingConfirmed(true);
    setModal("edit");
  };

  const deleteLoan = async (loan) => {
    const warning = loan.loan_status === "ISSUED" ? " This is an open loan, so the device will become available again." : "";
    if (!window.confirm(`Delete the lending record for ${loan.device_number} issued to ${loan.driver_name}?${warning}`)) return;
    try {
      const result = await axios.delete(`${API}/device-tracker/loans/${loan.loan_id}`, auth());
      toast(result.data.message || "Lending record deleted.");
      await load();
    } catch (error) {
      toast(error.response?.data?.message || "Could not delete lending record.", "error");
    }
  };

  return (
    <div className="dt-page">
      {notice && <div className={`dt-toast ${notice.kind}`}>{notice.text}</div>}
      <header className="dt-header">
        <div className="dt-heading">
          <button className="dt-icon-button" onClick={() => navigate("/main")} aria-label="Back">
            <FaArrowLeft />
          </button>
          <div>
            <h1>H2S & Thuraya Device Tracker</h1>
            <p>Controlled device lending, calibration expiry tracking, and inventory visibility</p>
          </div>
        </div>
        <div className="dt-header-actions">
          {tab === "inventory" && (
            <button className="dt-primary dt-add-device-btn" onClick={openAddDevice}>
              <FaPlus /> Add device
            </button>
          )}
          <button className="dt-primary" disabled={!available.length} onClick={() => { setBriefingConfirmed(false); setModal("lend"); }}>
            <FaPlus /> Lend device
          </button>
        </div>
      </header>
      <section className="dt-stats">
        <div>
          <span>Total inventory</span>
          <strong>{devices.length}</strong>
          <small>{inventoryTypeSummary || "0 items"}</small>
        </div>
        <div className="green">
          <span>Available now</span>
          <strong>{available.length}</strong>
          <small>Ready to issue</small>
        </div>
        <div className="blue">
          <span>With drivers</span>
          <strong>{active.length}</strong>
          <small>Open loans</small>
        </div>
        <div className={overdue.length ? "red" : "green"}>
          <span>Overdue</span>
          <strong>{overdue.length}</strong>
          <small>Past expected return</small>
        </div>
      </section>
      <section className="dt-panel">
        <div className="dt-tabs">
          <button className={tab === "active" ? "active" : ""} onClick={() => setTab("active")}>
            Active loans <b>{active.length}</b>
          </button>
          <button className={tab === "history" ? "active" : ""} onClick={() => setTab("history")}>
            All history
          </button>
          <button className={tab === "inventory" ? "active" : ""} onClick={() => setTab("inventory")}>
            Inventory
          </button>
        </div>
        <div className="dt-tools">
          <label>
            <FaSearch />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search device, SN, JP No, driver, phone or location" />
          </label>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="ALL">All device types</option>
            {deviceTypes.map((t) => (
              <option key={t} value={t}>
                {t === "H2S" ? "H2S Monitor" : t === "THURAYA" ? "THURAYA Phone" : t}
              </option>
            ))}
          </select>
          {tab === "inventory" && (
            <button className="dt-btn-add" onClick={openAddDevice}>
              <FaPlus /> Add device
            </button>
          )}
          <button onClick={exportRows}>
            <FaFileExcel /> Export
          </button>
        </div>
        {loading ? (
          <div className="dt-empty">Loading device records…</div>
        ) : filtered.length === 0 ? (
          <div className="dt-empty">
            <FaCheckCircle />
            <h3>No matching records</h3>
            <p>{tab === "active" ? "All devices are currently available." : "Try changing the search or filter."}</p>
          </div>
        ) : (
          <>
            <div className="dt-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Serial No.</th>
                    {tab === "inventory" ? (
                      <>
                        <th>Calibration Expiry</th>
                        <th>Calibration Status</th>
                        <th>Status</th>
                        <th>Operational</th>
                        <th>Action</th>
                      </>
                    ) : (
                      <>
                        <th>JP No.</th>
                        <th>Driver</th>
                        <th>Offloading location</th>
                        <th>Issued</th>
                        <th>Expected / returned</th>
                        <th>Status</th>
                        <th>Condition</th>
                        <th>Action</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const isOverdue = r.loan_status === "ISSUED" && r.expected_return_at && new Date(r.expected_return_at) < new Date();
                    const calStatus = getCalibrationStatus(r.calibration_expiry);
                    return (
                      <tr key={tab === "inventory" ? r.device_id : r.loan_id}>
                        <td>
                          <strong>{r.device_number}</strong>
                          <small>{r.device_type}</small>
                        </td>
                        <td>
                          {r.serial_number ? <span className="dt-sn-pill">{r.serial_number}</span> : <span className="dt-muted-dash">—</span>}
                        </td>
                        {tab === "inventory" ? (
                          <>
                            <td>{displayDateOnly(r.calibration_expiry)}</td>
                            <td>
                              <span className={`dt-badge ${calStatus.className}`}>
                                {calStatus.label}
                              </span>
                            </td>
                            <td>
                              <span className={`dt-badge ${(r.availability_status || "").toLowerCase()}`}>
                                {r.availability_status}
                              </span>
                            </td>
                            <td>{r.operational_status}</td>
                            <td>
                              <div className="dt-row-actions">
                                <button className="dt-edit" title="Edit device details" onClick={() => openEditDevice(r)}>
                                  <FaEdit /> Edit
                                </button>
                                {r.operational_status === "MAINTENANCE" && (
                                  <button className="dt-receive" title="Mark device ready for use" onClick={() => markReady(r)}>
                                    <FaCheckCircle /> Mark ready
                                  </button>
                                )}
                                <button className="dt-delete" title="Delete device from inventory" onClick={() => requestDeleteDevice(r)}>
                                  <FaTrash /> Delete
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>
                              {r.jp_number ? <span className="dt-jp-pill">{r.jp_number}</span> : <span className="dt-muted-dash">—</span>}
                            </td>
                            <td>
                              <strong>{r.driver_name}</strong>
                              <small>{r.phone_number}</small>
                            </td>
                            <td>{r.offloading_location}</td>
                            <td>{displayDate(r.issued_at)}</td>
                            <td>{displayDate(r.returned_at || r.expected_return_at)}</td>
                            <td>
                              <span className={`dt-badge ${isOverdue ? "overdue" : (r.loan_status || r.availability_status).toLowerCase()}`}>
                                {isOverdue ? "OVERDUE" : r.loan_status || r.availability_status}
                              </span>
                            </td>
                            <td>{r.condition_in || r.condition_out || r.operational_status}</td>
                            <td>
                              <div className="dt-row-actions">
                                {tab === "active" && (
                                  <button
                                    className="dt-receive"
                                    title="Receive device"
                                    onClick={() => {
                                      setSelected(r);
                                      setReceive({ returned_at: localInputValue(), condition_in: "GOOD", return_remarks: "" });
                                      setModal("receive");
                                    }}
                                  >
                                    <FaUndo /> Receive
                                  </button>
                                )}
                                <button className="dt-edit" title="Edit record" onClick={() => openEdit(r)}>
                                  <FaEdit /> Edit
                                </button>
                                <button className="dt-delete" title="Delete record" onClick={() => deleteLoan(r)}>
                                  <FaTrash /> Delete
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="dt-mobile-list">
              {filtered.map((r) => {
                const calStatus = getCalibrationStatus(r.calibration_expiry);
                return (
                  <article key={`m-${tab === "inventory" ? r.device_id : r.loan_id}`}>
                    <div className="dt-card-title">
                      <div>
                        <strong>{r.device_number}</strong>
                        <span>{r.device_type}</span>
                      </div>
                      <span className={`dt-badge ${(r.loan_status || r.availability_status).toLowerCase()}`}>
                        {r.loan_status || r.availability_status}
                      </span>
                    </div>
                    {tab === "inventory" ? (
                      <>
                        <div className="dt-card-grid">
                          <span>Serial No<b>{r.serial_number || "—"}</b></span>
                          <span>Cal. Expiry<b>{displayDateOnly(r.calibration_expiry)}</b></span>
                          <span>Cal. Status<b className={calStatus.className}>{calStatus.label}</b></span>
                          <span>Operational<b>{r.operational_status}</b></span>
                        </div>
                        <div className="dt-mobile-actions">
                          <button className="dt-edit" onClick={() => openEditDevice(r)}>
                            <FaEdit /> Edit
                          </button>
                          {r.operational_status === "MAINTENANCE" && (
                            <button className="dt-receive" onClick={() => markReady(r)}>
                              <FaCheckCircle /> Mark ready
                            </button>
                          )}
                          <button className="dt-delete" onClick={() => requestDeleteDevice(r)}>
                            <FaTrash /> Delete
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="dt-card-grid">
                          <span>JP No<b>{r.jp_number || "—"}</b></span>
                          <span>Serial No<b>{r.serial_number || "—"}</b></span>
                          <span>Driver<b>{r.driver_name}</b></span>
                          <span>Phone<b>{r.phone_number}</b></span>
                          <span>Location<b>{r.offloading_location}</b></span>
                          <span>Issued<b>{displayDate(r.issued_at)}</b></span>
                        </div>
                        <div className="dt-mobile-actions">
                          {tab === "active" && (
                            <button
                              className="dt-receive"
                              onClick={() => {
                                setSelected(r);
                                setReceive({ returned_at: localInputValue(), condition_in: "GOOD", return_remarks: "" });
                                setModal("receive");
                              }}
                            >
                              <FaUndo /> Receive
                            </button>
                          )}
                          <button className="dt-edit" onClick={() => openEdit(r)}>
                            <FaEdit /> Edit
                          </button>
                          <button className="dt-delete" onClick={() => deleteLoan(r)}>
                            <FaTrash /> Delete
                          </button>
                        </div>
                      </>
                    )}
                  </article>
                );
              })}
            </div>
          </>
        )}
      </section>

      {/* Lend / Edit Loan Modal */}
      {modal && (
        <div className="dt-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="dt-modal">
            <button className="dt-close" onClick={() => setModal(null)}>×</button>
            {modal !== "receive" ? (
              <form onSubmit={submitLend}>
                <div className="dt-modal-head">
                  <FaPlus />
                  <div>
                    <h2>{modal === "edit" ? "Edit lending record" : "Lend a device"}</h2>
                    <p>{modal === "edit" ? `Correct the details for ${selected?.device_number}.` : "Only currently available devices can be selected."}</p>
                  </div>
                </div>
                <div className="dt-form-grid">
                  <label>Available device *
                    <select
                      required
                      disabled={modal === "edit"}
                      value={lend.device_id}
                      onChange={(e) => {
                        setLend({ ...lend, device_id: e.target.value });
                        setBriefingConfirmed(false);
                      }}
                    >
                      <option value="">Select a device</option>
                      {modal === "edit" && selected && (
                        <option value={selected.device_id}>
                          {selected.device_number} — {selected.device_type}
                          {selected.serial_number ? ` | SN: ${selected.serial_number}` : ""}
                          {selected.calibration_expiry ? ` | Exp: ${displayDateOnly(selected.calibration_expiry)}` : ""}
                        </option>
                      )}
                      {available
                        .filter((d) => modal !== "edit" || d.device_id !== selected?.device_id)
                        .map((d) => {
                          const calStatus = getCalibrationStatus(d.calibration_expiry);
                          const isExpired = calStatus.className === "cal-expired";
                          return (
                            <option key={d.device_id} value={d.device_id}>
                              {d.device_number} — {d.device_type}
                              {d.serial_number ? ` | SN: ${d.serial_number}` : ""}
                              {d.calibration_expiry ? ` | Exp: ${displayDateOnly(d.calibration_expiry)}` : ""}
                              {isExpired ? " ⚠️ [EXPIRED]" : ""}
                            </option>
                          );
                        })}
                    </select>
                  </label>
                  <label>Journey Plan No (JP No)
                    <input
                      type="text"
                      placeholder="e.g. JP-100234 (optional)"
                      maxLength="50"
                      value={lend.jp_number}
                      onChange={(e) => setLend({ ...lend, jp_number: e.target.value })}
                    />
                  </label>
                  <label className="dt-driver-field">Driver name *
                    <div className="dt-driver-select">
                      <input
                        required
                        autoComplete="off"
                        placeholder="Search and select driver"
                        value={driverSearch}
                        onFocus={() => setDriverMenuOpen(true)}
                        onChange={(e) => {
                          setDriverSearch(e.target.value);
                          setLend({ ...lend, driver_name: "" });
                          setDriverMenuOpen(true);
                        }}
                      />
                      {driverMenuOpen && (
                        <div className="dt-driver-menu">
                          {filteredDrivers.length ? filteredDrivers.slice(0, 50).map((driver) => (
                            <button
                              type="button"
                              key={driver.driver_id}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => selectDriver(driver)}
                            >
                              <span>{driver.driver_name}</span>
                              <small>{driver.reporting_driver_code || "No driver code"}</small>
                            </button>
                          )) : <p>No active drivers found</p>}
                        </div>
                      )}
                    </div>
                  </label>
                  <label>Phone number *
                    <input
                      required
                      type="tel"
                      pattern="[0-9+() -]{7,25}"
                      value={lend.phone_number}
                      onChange={(e) => setLend({ ...lend, phone_number: e.target.value })}
                    />
                  </label>
                  <label>Offloading location *
                    <input
                      required
                      maxLength="200"
                      value={lend.offloading_location}
                      onChange={(e) => setLend({ ...lend, offloading_location: e.target.value })}
                    />
                  </label>
                  <label>Date & time given *
                    <input
                      required
                      type="datetime-local"
                      value={lend.issued_at}
                      onChange={(e) => setLend({ ...lend, issued_at: e.target.value })}
                    />
                  </label>
                  <label>Expected return
                    <input
                      type="datetime-local"
                      min={lend.issued_at}
                      value={lend.expected_return_at}
                      onChange={(e) => setLend({ ...lend, expected_return_at: e.target.value })}
                    />
                  </label>
                  <label>Condition when issued *
                    <select
                      value={lend.condition_out}
                      onChange={(e) => setLend({ ...lend, condition_out: e.target.value })}
                    >
                      <option>GOOD</option>
                      <option>FAIR</option>
                      <option>DAMAGED</option>
                    </select>
                  </label>
                  <label className="wide">Remarks
                    <textarea
                      maxLength="500"
                      value={lend.remarks}
                      onChange={(e) => setLend({ ...lend, remarks: e.target.value })}
                    />
                  </label>
                </div>

                {modal !== "edit" && (isLendH2S || isLendThuraya) && (
                  <div className="wide dt-briefing-box">
                    <div className="dt-briefing-header">
                      <FaShieldAlt />
                      <div>
                        <h4>{isLendH2S ? "1. H₂S Monitor Briefing – Before Handover" : "2. Thuraya Satellite Phone Briefing – Before Handover"}</h4>
                        <p>The JM must brief the driver on the following points before handing over this equipment:</p>
                      </div>
                    </div>
                    <ul className="dt-briefing-list">
                      {isLendH2S ? (
                        <>
                          <li>Where the driver is going and the H₂S risk at the destination.</li>
                          <li>How to use the H₂S device/monitor.</li>
                          <li>How to respond in case of an H₂S alarm.</li>
                        </>
                      ) : (
                        <>
                          <li>Why and where the Thuraya satellite phone is required.</li>
                          <li>How to operate and use the Thuraya phone.</li>
                          <li>How to make an emergency call when there is no mobile network coverage.</li>
                        </>
                      )}
                    </ul>
                    <label className="dt-briefing-confirm">
                      <input
                        type="checkbox"
                        required
                        checked={briefingConfirmed}
                        onChange={(e) => setBriefingConfirmed(e.target.checked)}
                      />
                      <span>
                        <strong>JM Confirmation:</strong> I confirm that the driver has been briefed on {isLendH2S ? "the H₂S requirements before the H₂S device/monitor was handed over." : "the use of the Thuraya satellite phone before the phone was handed over."} <em className="dt-required">*</em>
                      </span>
                    </label>
                  </div>
                )}

                <div className="dt-actions">
                  <button type="button" onClick={() => setModal(null)}>Cancel</button>
                  <button className="dt-primary" disabled={saving}>
                    {saving ? "Saving…" : modal === "edit" ? "Save changes" : "Confirm lending"}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={submitReceive}>
                <div className="dt-modal-head">
                  <FaUndo />
                  <div>
                    <h2>Receive {selected?.device_number}</h2>
                    <p>Close the loan only after physically receiving the device.</p>
                  </div>
                </div>
                <div className="dt-callout">
                  <FaExclamationTriangle /> Issued to <strong>{selected?.driver_name}</strong> for {selected?.offloading_location}
                </div>
                <div className="dt-form-grid one">
                  <label>Return date & time *
                    <input
                      required
                      type="datetime-local"
                      value={receive.returned_at}
                      onChange={(e) => setReceive({ ...receive, returned_at: e.target.value })}
                    />
                  </label>
                  <label>Condition on return *
                    <select
                      value={receive.condition_in}
                      onChange={(e) => setReceive({ ...receive, condition_in: e.target.value })}
                    >
                      <option>GOOD</option>
                      <option>FAIR</option>
                      <option>DAMAGED</option>
                      <option>LOST</option>
                    </select>
                  </label>
                  <label className="wide">Return remarks
                    <textarea
                      required={receive.condition_in !== "GOOD"}
                      placeholder={receive.condition_in !== "GOOD" ? "Required for non-good condition" : "Optional notes"}
                      maxLength="500"
                      value={receive.return_remarks}
                      onChange={(e) => setReceive({ ...receive, return_remarks: e.target.value })}
                    />
                  </label>
                </div>
                <div className="dt-actions">
                  <button type="button" onClick={() => setModal(null)}>Cancel</button>
                  <button className="dt-primary" disabled={saving}>{saving ? "Closing…" : "Confirm received"}</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Edit Device Inventory Modal */}
      {deviceModal && (
        <div className="dt-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setDeviceModal(false)}>
          <div className="dt-modal">
            <button className="dt-close" onClick={() => setDeviceModal(false)}>×</button>
            <form onSubmit={submitEditDevice}>
              <div className="dt-modal-head">
                <FaEdit />
                <div>
                  <h2>Edit Device ({deviceForm.device_number})</h2>
                  <p>Update device serial number, calibration expiry, and operational state.</p>
                </div>
              </div>
              <div className="dt-form-grid">
                <label>Device Number
                  <input type="text" disabled value={deviceForm.device_number} />
                </label>
                <label>Device Type *
                  <select
                    value={deviceForm.device_type}
                    onChange={(e) => setDeviceForm({ ...deviceForm, device_type: e.target.value })}
                  >
                    {deviceTypes.map((t) => (
                      <option key={t} value={t}>
                        {t === "H2S" ? "H2S Monitor" : t === "THURAYA" ? "THURAYA Phone" : t}
                      </option>
                    ))}
                    <option value="NEW_TYPE">+ Add New Device Type...</option>
                  </select>
                </label>
                {deviceForm.device_type === "NEW_TYPE" && (
                  <label className="wide dt-custom-type-field">
                    Enter New Device Type Name *
                    <input
                      required
                      type="text"
                      placeholder="e.g. MULTI-GAS, SATELLITE GPS, RADIO"
                      maxLength="50"
                      value={deviceForm.custom_type || ""}
                      onChange={(e) => setDeviceForm({ ...deviceForm, custom_type: e.target.value })}
                      autoFocus
                    />
                  </label>
                )}
                <label className="wide">Serial Number
                  <input
                    type="text"
                    placeholder="e.g. SN-H2S-98421"
                    maxLength="100"
                    value={deviceForm.serial_number}
                    onChange={(e) => setDeviceForm({ ...deviceForm, serial_number: e.target.value })}
                  />
                </label>
                <label>Calibration Expiry Date
                  <input
                    type="date"
                    value={deviceForm.calibration_expiry}
                    onChange={(e) => setDeviceForm({ ...deviceForm, calibration_expiry: e.target.value })}
                  />
                </label>
                <label>Operational Status
                  <select
                    value={deviceForm.operational_status}
                    onChange={(e) => setDeviceForm({ ...deviceForm, operational_status: e.target.value })}
                  >
                    <option value="ACTIVE">ACTIVE (Ready for operations)</option>
                    <option value="MAINTENANCE">MAINTENANCE (Out for calibration / repair)</option>
                    <option value="RETIRED">RETIRED (Decommissioned)</option>
                  </select>
                </label>
              </div>
              <div className="dt-actions">
                <button type="button" onClick={() => setDeviceModal(false)}>Cancel</button>
                <button className="dt-primary" disabled={saving}>{saving ? "Saving…" : "Save Device"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Device Inventory Modal */}
      {addDeviceModal && (
        <div className="dt-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setAddDeviceModal(false)}>
          <div className="dt-modal">
            <button className="dt-close" onClick={() => setAddDeviceModal(false)}>×</button>
            <form onSubmit={submitAddDevice}>
              <div className="dt-modal-head">
                <FaPlus />
                <div>
                  <h2>Add New Device to Inventory</h2>
                  <p>Register an H₂S monitor, Thuraya phone, or any new equipment type into inventory.</p>
                </div>
              </div>
              <div className="dt-form-grid">
                <label>Device Number / Tag *
                  <input
                    required
                    type="text"
                    placeholder="e.g. H2S-11 or THU-05"
                    maxLength="50"
                    value={newDevice.device_number}
                    onChange={(e) => setNewDevice({ ...newDevice, device_number: e.target.value })}
                  />
                </label>
                <label>Device Type *
                  <select
                    required
                    value={newDevice.device_type}
                    onChange={(e) => setNewDevice({ ...newDevice, device_type: e.target.value })}
                  >
                    {deviceTypes.map((t) => (
                      <option key={t} value={t}>
                        {t === "H2S" ? "H2S Monitor" : t === "THURAYA" ? "THURAYA Phone" : t}
                      </option>
                    ))}
                    <option value="NEW_TYPE">+ Add New Device Type...</option>
                  </select>
                </label>
                {newDevice.device_type === "NEW_TYPE" && (
                  <label className="wide dt-custom-type-field">
                    Enter New Device Type Name *
                    <input
                      required
                      type="text"
                      placeholder="e.g. MULTI-GAS, SATELLITE GPS, RADIO, VHF"
                      maxLength="50"
                      value={newDevice.custom_type}
                      onChange={(e) => setNewDevice({ ...newDevice, custom_type: e.target.value })}
                      autoFocus
                    />
                  </label>
                )}
                <label className="wide">Serial Number(s)
                  <input
                    type="text"
                    placeholder="e.g. H2S Monitor A254200524, H2S Hood 3663 (optional)"
                    maxLength="150"
                    value={newDevice.serial_number}
                    onChange={(e) => setNewDevice({ ...newDevice, serial_number: e.target.value })}
                  />
                </label>
                <label>Calibration Expiry Date
                  <input
                    type="date"
                    value={newDevice.calibration_expiry}
                    onChange={(e) => setNewDevice({ ...newDevice, calibration_expiry: e.target.value })}
                  />
                </label>
                <label>Initial Operational Status *
                  <select
                    value={newDevice.operational_status}
                    onChange={(e) => setNewDevice({ ...newDevice, operational_status: e.target.value })}
                  >
                    <option value="ACTIVE">ACTIVE (Ready to issue)</option>
                    <option value="MAINTENANCE">MAINTENANCE (Out for calibration / repair)</option>
                    <option value="RETIRED">RETIRED (Not in active use)</option>
                  </select>
                </label>
              </div>
              <div className="dt-actions">
                <button type="button" onClick={() => setAddDeviceModal(false)}>Cancel</button>
                <button className="dt-primary" disabled={saving}>{saving ? "Saving…" : "Add Device"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Device Confirmation Modal Window */}
      {deviceToDelete && (
        <div className="dt-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setDeviceToDelete(null)}>
          <div className="dt-modal dt-confirm-modal">
            <button className="dt-close" onClick={() => setDeviceToDelete(null)}>×</button>
            <div className="dt-modal-head dt-modal-head-danger">
              <FaExclamationTriangle />
              <div>
                <h2>Delete Device</h2>
                <p>Confirm removing device from inventory.</p>
              </div>
            </div>
            <div className="dt-confirm-body">
              <p>
                Are you sure you want to permanently delete <strong>{deviceToDelete.device_number}</strong> ({deviceToDelete.device_type}) from the inventory?
              </p>
              <div className="dt-confirm-details">
                <div className="dt-confirm-row">
                  <span>Device Tag:</span>
                  <strong>{deviceToDelete.device_number}</strong>
                </div>
                <div className="dt-confirm-row">
                  <span>Equipment Type:</span>
                  <b>{deviceToDelete.device_type}</b>
                </div>
                <div className="dt-confirm-row">
                  <span>Serial Number:</span>
                  <span>{deviceToDelete.serial_number || "—"}</span>
                </div>
                <div className="dt-confirm-row">
                  <span>Calibration Expiry:</span>
                  <span>{displayDateOnly(deviceToDelete.calibration_expiry)}</span>
                </div>
                <div className="dt-confirm-row">
                  <span>Status:</span>
                  <span className={`dt-badge ${(deviceToDelete.availability_status || "").toLowerCase()}`}>
                    {deviceToDelete.availability_status}
                  </span>
                </div>
              </div>
              <div className="dt-confirm-caution">
                <FaExclamationTriangle /> This action is permanent and cannot be undone.
              </div>
            </div>
            <div className="dt-actions">
              <button type="button" onClick={() => setDeviceToDelete(null)}>Cancel</button>
              <button className="dt-danger-btn" disabled={saving} onClick={confirmDeleteDevice}>
                <FaTrash /> {saving ? "Deleting…" : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceTracker;
